import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, FileDown, ScrollText, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * Renders a "Generate Salary Certificate" button that opens a small dialog
 * to pick a Financial Year (Apr–Mar Indian FY), an optional manual override
 * for the annual gross, and whether to include the monthly breakdown
 * table. On submit, downloads the PDF stream directly.
 *
 * Contract:
 *  - `employeeId` : id of the employee row (required)
 *  - `employeeName` : full name for filename fallback + dialog copy
 *  - `annualCtc` : current CTC (used to prefill the override placeholder)
 *
 * Backend: POST /api/payroll/employees/{id}/salary-certificate
 * Access: HR / super-admin only (permission enforced server-side).
 */
const buildFYOptions = () => {
  // Current FY runs Apr → Mar. We show the current + previous 4 FYs so
  // users can back-issue certificates for tax filings.
  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1..12
  const currentFYStart = currentMonth >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  const options = [];
  for (let i = 0; i < 5; i++) {
    const start = currentFYStart - i;
    const end = String(start + 1).slice(-2);
    options.push({ value: `${start}-${end}`, label: `${start}-${end}` });
  }
  return options;
};

export function SalaryCertificateDialog({ employeeId, employeeName, annualCtc, employeeEmail, disabled, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [fy, setFy] = useState(() => buildFYOptions()[0].value);
  const [override, setOverride] = useState('');
  const [includeBreakdown, setIncludeBreakdown] = useState(true);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const fyOptions = useMemo(() => buildFYOptions(), []);

  const validateInputs = () => {
    if (!fy) {
      toast.error('Please pick a Financial Year');
      return null;
    }
    const overrideNum = override.trim() ? parseFloat(override) : null;
    if (override.trim() && (Number.isNaN(overrideNum) || overrideNum < 0)) {
      toast.error('Override amount must be a positive number');
      return null;
    }
    return { fy, override_amount: overrideNum, include_breakdown: includeBreakdown };
  };

  const handleGenerate = async () => {
    const payload = validateInputs();
    if (!payload) return;
    setBusy(true);
    try {
      const res = await api.post(
        `/payroll/employees/${employeeId}/salary-certificate`,
        payload,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cleanName = (employeeName || 'employee').replace(/\s+/g, '-');
      link.download = `Salary-Certificate-${cleanName}-FY-${fy}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Salary certificate for FY ${fy} downloaded`);
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      let detail = 'Failed to generate certificate';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || detail;
        } catch { /* leave default */ }
      } else if (data?.detail) {
        detail = data.detail;
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const handleEmail = async () => {
    const payload = validateInputs();
    if (!payload) return;
    if (!employeeEmail) {
      toast.error('Employee has no official email on record. Add one before emailing.');
      return;
    }
    setEmailing(true);
    try {
      const res = await api.post(
        `/payroll/employees/${employeeId}/salary-certificate/email`,
        payload,
      );
      toast.success(res.data?.message || `Certificate emailed to ${employeeEmail}`);
      setOpen(false);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to email certificate';
      toast.error(detail);
    } finally {
      setEmailing(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid="generate-salary-cert-btn"
      >
        <ScrollText className="h-4 w-4 mr-2" />
        Generate Salary Certificate
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy && !emailing) setOpen(o); }}>
        <DialogContent className="max-w-lg overflow-x-hidden" data-testid="salary-cert-dialog">
          <DialogHeader>
            <DialogTitle>Annual Salary Certificate</DialogTitle>
            <DialogDescription>
              Issue a formal salary certificate for {employeeName || 'this employee'} for an
              Indian Financial Year (April&nbsp;–&nbsp;March).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Financial Year</Label>
              <Select value={fy} onValueChange={setFy}>
                <SelectTrigger data-testid="salary-cert-fy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {fyOptions.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                We&apos;ll pull the payroll rows that fall inside this FY. If none exist, the
                employee&apos;s current CTC is used as a fallback.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Manual Override — Annual Gross (INR)</Label>
              <Input
                type="number"
                min="0"
                value={override}
                onChange={e => setOverride(e.target.value)}
                placeholder={annualCtc ? `Leave blank to use ${annualCtc}` : 'Optional'}
                data-testid="salary-cert-override"
              />
              <p className="text-[11px] text-muted-foreground">
                Optional. When set, the certificate&apos;s annual gross is pinned to this amount
                and the monthly breakdown is rescaled proportionally.
              </p>
            </div>

            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <Label>Include monthly breakdown</Label>
                <p className="text-[11px] text-muted-foreground">
                  Add a table with each month&apos;s Basic, HRA, Allowances, Deductions and
                  Net.
                </p>
              </div>
              <Switch
                checked={includeBreakdown}
                onCheckedChange={setIncludeBreakdown}
                data-testid="salary-cert-breakdown"
              />
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy || emailing} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleEmail}
              disabled={busy || emailing || !employeeEmail}
              title={!employeeEmail ? 'Employee has no email on record' : `Email to ${employeeEmail}`}
              data-testid="salary-cert-email"
              className="w-full sm:w-auto"
            >
              {emailing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Email
            </Button>
            <Button onClick={handleGenerate} disabled={busy || emailing} data-testid="salary-cert-download" className="w-full sm:w-auto">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
